import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usePartnerProfile, useUpdatePartnerProfile, useAddPixKey } from '../hooks/usePartnerPayouts';
import { Button, Input, FormField, Card, CardContent, CardHeader, CardTitle, CardDescription, Badge } from '@medikei/ui';
import { Spinner } from '@medikei/ui';
import { User, Mail, CreditCard, Shield, CheckCircle, AlertCircle } from 'lucide-react';

const profileSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  phone: z.string().min(10, 'Telefone inválido'),
});

const pixKeySchema = z.object({
  type: z.enum(['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP']),
  key: z.string().min(1, 'Chave PIX é obrigatória'),
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PixKeyFormData = z.infer<typeof pixKeySchema>;

export function PartnerProfilePage() {
  const [editingProfile, setEditingProfile] = useState(false);
  const [addingPixKey, setAddingPixKey] = useState(false);

  const { data: profile, isLoading, error } = usePartnerProfile();
  const updateProfile = useUpdatePartnerProfile();
  const addPixKey = useAddPixKey();

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: profile?.user?.name || '',
      phone: '',
    },
  });

  const pixKeyForm = useForm<PixKeyFormData>({
    resolver: zodResolver(pixKeySchema),
  });

  const handleProfileSubmit = async (data: ProfileFormData) => {
    try {
      await updateProfile.mutateAsync(data);
      setEditingProfile(false);
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
    }
  };

  const handlePixKeySubmit = async (data: PixKeyFormData) => {
    try {
      await addPixKey.mutateAsync(data);
      setAddingPixKey(false);
      pixKeyForm.reset();
    } catch (error) {
      console.error('Erro ao adicionar chave PIX:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-600">Erro ao carregar perfil.</p>
      </div>
    );
  }

  const kycStatusConfig: Record<string, { variant: 'warning' | 'success' | 'danger'; label: string; icon: typeof AlertCircle }> = {
    PENDING: { variant: 'warning', label: 'Pendente', icon: AlertCircle },
    SUBMITTED: { variant: 'warning', label: 'Enviado', icon: AlertCircle },
    APPROVED: { variant: 'success', label: 'Verificado', icon: CheckCircle },
    REJECTED: { variant: 'danger', label: 'Rejeitado', icon: AlertCircle },
  };

  const KycIcon = kycStatusConfig[profile.kycStatus]?.icon || AlertCircle;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Perfil</h1>
        <p className="text-muted-foreground mt-1">
          Gerencie suas informações pessoais e KYC
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Informações Pessoais
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!editingProfile ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                  <User className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Nome</p>
                    <p className="font-medium">{profile.user?.name || 'Não informado'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">E-mail</p>
                    <p className="font-medium">{profile.user?.email || 'Não informado'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                  <CreditCard className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">CPF</p>
                    <p className="font-medium">•••.•••.•••-••</p>
                  </div>
                </div>

                <Button onClick={() => setEditingProfile(true)} className="w-full">
                  Editar Informações
                </Button>
              </div>
            ) : (
              <form onSubmit={profileForm.handleSubmit(handleProfileSubmit)} className="space-y-4">
                <FormField label="Nome" error={profileForm.formState.errors.name?.message}>
                  <Input {...profileForm.register('name')} />
                </FormField>

                <FormField label="Telefone" error={profileForm.formState.errors.phone?.message}>
                  <Input {...profileForm.register('phone')} />
                </FormField>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setEditingProfile(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    loading={updateProfile.isPending}
                    className="flex-1"
                  >
                    Salvar
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Status KYC
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                profile.kycStatus === 'APPROVED' ? 'bg-emerald-100' : 'bg-amber-100'
              }`}>
                <KycIcon className={`w-5 h-5 ${
                  profile.kycStatus === 'APPROVED' ? 'text-emerald-600' : 'text-amber-600'
                }`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status da Verificação</p>
                <Badge variant={kycStatusConfig[profile.kycStatus]?.variant || 'warning'}>
                  {kycStatusConfig[profile.kycStatus]?.label || 'Pendente'}
                </Badge>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Documentos Enviados</p>
              <p className="text-sm text-muted-foreground">Nenhum documento enviado</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Dados Bancários
            </CardTitle>
            <CardDescription>
              Configure sua chave PIX ou conta bancária para receber saques
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Nenhuma chave PIX cadastrada
            </p>

            {!addingPixKey ? (
              <Button onClick={() => setAddingPixKey(true)} className="w-full">
                Adicionar Chave PIX
              </Button>
            ) : (
              <form onSubmit={pixKeyForm.handleSubmit(handlePixKeySubmit)} className="space-y-4">
                <FormField label="Tipo de Chave">
                  <select
                    {...pixKeyForm.register('type')}
                    className="w-full h-11 px-4 rounded-full border border-neutral-200 bg-white text-sm"
                  >
                    <option value="CPF">CPF</option>
                    <option value="CNPJ">CNPJ</option>
                    <option value="EMAIL">E-mail</option>
                    <option value="PHONE">Telefone</option>
                    <option value="EVP">EVP</option>
                  </select>
                </FormField>

                <FormField label="Chave PIX" error={pixKeyForm.formState.errors.key?.message}>
                  <Input {...pixKeyForm.register('key')} placeholder="Digite sua chave PIX" />
                </FormField>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setAddingPixKey(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    loading={addPixKey.isPending}
                    className="flex-1"
                  >
                    Salvar
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Status da Conta</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge
                  variant={profile.status === 'ACTIVE' ? 'success' : profile.status === 'SUSPENDED' ? 'danger' : 'warning'}
                >
                  {profile.status === 'ACTIVE' ? 'Ativo' : profile.status === 'SUSPENDED' ? 'Suspenso' : 'Pendente'}
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Nível</span>
                <Badge variant="outline">{profile.level || 'BRONZE'}</Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Membro desde</span>
                <span className="text-sm font-medium">
                  {new Date(profile.createdAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}